"use client";

import { Target, Activity, Eye, ArrowRight, Layers } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConsultingProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const phases = [{
    step: "01",
    title: "AUDIT",
    description: "Comprehensive analysis of your current revenue streams, OTA performance, and digital footprint.",
    Icon: Target
  }, {
    step: "02",
    title: "STRATEGIZE",
    description: "Develop data-backed revenue management plans tailored to your property's unique market position.",
    Icon: Activity
  }, {
    step: "03",
    title: "OPTIMIZE",
    description: "Fine-tune OTA listings, pricing algorithms, and tech stack integrations for maximum yield.",
    Icon: Eye
  }, {
    step: "04",
    title: "EXECUTE",
    description: "Implement high-conversion strategies and direct booking campaigns to drive sustainable growth.",
    Icon: ArrowRight
  }, {
    step: "05",
    title: "SCALE",
    description: "Continuous monitoring and refinement to ensure long-term revenue stability and market dominance.",
    Icon: Layers
  }];
  return <section data-section-id="5079" id="consultingprocess" className="relative bg-slate-50 text-slate-900 py-24 md:py-40 px-4 sm:px-6 lg:px-12 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      <div ref={ref} className={`relative max-w-7xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="flex flex-col items-center text-center mb-16 md:mb-24">
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-10 bg-blue-600" />
            <span className="font-default text-xs tracking-[0.3em] text-blue-600 uppercase">
              04 / Process
            </span>
            <span className="h-px w-10 bg-blue-600" />
          </div>

          <h2 className="font-default font-bold uppercase tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-slate-900 leading-[0.95] max-w-5xl">
            Our Revenue Framework.
          </h2>

          <p className="font-default text-base md:text-lg text-slate-600 mt-8 max-w-xl leading-relaxed">
            A five-phase consulting framework engineered to transform your hotel's performance through data-driven revenue management and OTA optimization.
          </p>
        </div>

        {}
        <div className="hidden lg:grid grid-cols-5 gap-0 relative">
          {phases.map((phase, i) => {
          const isActive = i === 1;
          const Icon = phase.Icon;
          return <div data-index={i} key={phase.step} className="relative group px-3">
                {}
                {i < phases.length - 1 && <div aria-hidden="true" className="absolute top-1/2 -right-3 w-6 h-px z-0" style={{
              backgroundImage: "repeating-linear-gradient(to right, var(--primary) 0 4px, transparent 4px 10px)"
            }} />}

                <div className={`relative h-[340px] backdrop-blur-sm bg-white border rounded-lg ${isActive ? "border-blue-600 shadow-lg" : "border-slate-200"} shadow-md p-6 flex flex-col justify-between transition-all duration-300 group-hover:border-blue-600 group-hover:-translate-y-2 group-hover:shadow-lg`}>
                  <div>
                    <div className="font-default text-5xl font-normal text-slate-300 leading-none">
                      {phase.step}
                    </div>
                    <div className={`h-px w-12 mt-4 mb-6 ${isActive ? "bg-blue-600" : "bg-slate-200 group-hover:bg-blue-600"} transition-colors duration-300`} />
                    <h3 className="font-default font-semibold text-xl md:text-[22px] uppercase tracking-tight text-slate-900 mb-4">
                      {phase.title}
                    </h3>
                    <p className="font-default text-sm leading-relaxed text-slate-600">
                      {phase.description}
                    </p>
                  </div>

                  <div className="flex justify-end mt-6">
                    <Icon className="size-7 text-blue-600" strokeWidth={1.25} />
                  </div>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="lg:hidden flex flex-col gap-0 max-w-md mx-auto">
          {phases.map((phase, i) => {
          const isActive = i === 1;
          const Icon = phase.Icon;
          return <div data-index={i} key={phase.step} className="relative">
                <div className={`relative backdrop-blur-sm bg-white border rounded-lg ${isActive ? "border-blue-600 shadow-lg" : "border-slate-200"} shadow-md p-6 flex flex-col transition-all duration-300 hover:border-blue-600 hover:shadow-lg`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="font-default text-4xl font-normal text-slate-300 leading-none">
                      {phase.step}
                    </div>
                    <Icon className="size-6 text-blue-600" strokeWidth={1.25} />
                  </div>
                  <div className={`h-px w-12 mb-4 ${isActive ? "bg-blue-600" : "bg-slate-200"}`} />
                  <h3 className="font-default font-semibold text-xl uppercase tracking-tight text-slate-900 mb-3">
                    {phase.title}
                  </h3>
                  <p className="font-default text-sm leading-relaxed text-slate-600">
                    {phase.description}
                  </p>
                </div>

                {}
                {i < phases.length - 1 && <div aria-hidden="true" className="mx-auto w-px h-10" style={{
              backgroundImage: "repeating-linear-gradient(to bottom, var(--primary) 0 4px, transparent 4px 10px)"
            }} />}
              </div>;
        })}
        </div>
      </div>
    </section>;
}